<template>
  <div class="toast-container" :class="toastType" :style="`--toast-duration: ${duration}s;`" :ref="id" @click="() => execClick()">
    <div class="relative p-3">
      <button
        v-if="typeof closeable === 'undefined' || closeable"
        class="absolute top-0 right-0 cursor-pointer p-2 hover:opacity-60"
        title="Close"
        @click.stop="() => close()"
      >
        <UiIcon icon="x" />
      </button>

      <div class="flex items-center gap-2">
        <pe-icon :size="24">{{ toastIcon }}</pe-icon>
        <div class="space-y-2">
          <div v-if="title !== null" class="font-semibold" v-html="title || 'Atenção'"></div>
          <div v-html="message"></div>
        </div>
      </div>

      <div v-if="autoClose" class="progress"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import UiIcon from '@/components/ui/icon.vue'

const props = defineProps<{
  id: string
  type?: string
  title?: string
  message?: string
  autoClose?: boolean
  closeable?: boolean
  duration?: number
  icon?: string
  onClick?: Function
}>()

const emit = defineEmits(['close'])

const timer = ref<any>(-1)
const startedAt = ref<number>(0)
const delay = ref<number>(0)

onMounted(() => {
  if (props.autoClose) {
    startedAt.value = Date.now()
    delay.value = (props.duration || 5) * 1000
    timer.value = setTimeout(close, delay.value)
  }
})

const toastIcon = computed(() => {
  if (props.icon) return props.icon
  switch (props.type) {
    case 'error':
      return 'error'
    case 'warning':
      return 'warning'
    case 'success':
      return 'check_circle'
    default:
      return 'info'
  }
})

const toastType = computed(() => {
  const tp = props.type.toLocaleLowerCase()
  const opts = ['error', 'warning', 'success', 'info', 'default']

  return `t-${opts.includes(tp) ? tp : 'default'}`
})

const execClick = () => {
  if (props.onClick && typeof props.onClick === 'function') {
    props.onClick()
  }
}

const close = () => {
  emit('close')
}
</script>

<style lang="scss" scoped>
.toast-container {
  @apply max-w-[368px] overflow-hidden rounded-lg bg-white;

  .progress {
    @apply absolute bottom-0 left-0 h-[4px] w-full;
    animation: progress var(--toast-duration) ease-in-out forwards;
  }

  &.t-error {
    @apply bg-red-200 text-red-600;
    .progress {
      @apply bg-red-500;
    }
  }
  &.t-warning {
    @apply bg-amber-200 text-amber-600;
    .progress {
      @apply bg-amber-500;
    }
  }
  &.t-success {
    @apply bg-emerald-200 text-emerald-600;
    .progress {
      @apply bg-emerald-500;
    }
  }
  &.t-info {
    @apply bg-sky-200 text-sky-600;
    .progress {
      @apply bg-sky-500;
    }
  }
}

@keyframes progress {
  to {
    width: 0;
  }
}
</style>
